import { Button, Col, Form, FormCheck, FormControl, FormLabel, FormSelect, Row } from "react-bootstrap";
import { ISiteMenuGroup } from "../../../models/Interfaces";
import { useCallback, useEffect, useRef, useState } from "react";
import { createFormValidation } from "../../../helpers/formValidationHelper";
import SiteMediaPicker from "../../components/common/SiteMediaPicker";
import ColourPicker from "../../components/common/ColourPicker";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSave } from "@fortawesome/free-solid-svg-icons";
import FormCheckInput from "react-bootstrap/esm/FormCheckInput";
import { Core, formValidation, notEmpty, callback } from "@form-validation/bundle/full";
import { Bootstrap5 } from "@form-validation/plugin-bootstrap5";
import { Trigger } from "@form-validation/plugin-trigger";
import { SubmitButton } from "@form-validation/plugin-submit-button";
import { Message } from "@form-validation/plugin-message";

interface IMenuGroupEditor {
  selectedGroupItem: ISiteMenuGroup,
  onClose: () => void;
  onSave: (mengroup: ISiteMenuGroup) => void;
  asModal?: boolean;
}

const MenuGroupEditor = (props: IMenuGroupEditor) => {

  const [selectedGroupItem, setSelectedGroupItem ] = useState<ISiteMenuGroup | null>(null);

  const formRef = useRef<HTMLFormElement | null>(null);
  const fvRef = useRef<Core | null>(null);

  const updateGroupProperty = (name: string, value: string | boolean | null) : void => {
    if (value === null || value === undefined) {
      value = "";
    }
    
    if (selectedGroupItem) {
      setSelectedGroupItem(prev => ({
        ...prev as ISiteMenuGroup,
        [name]: value
      }));
    }
  };

  const handleSave = async () => {
    if (fvRef.current && selectedGroupItem && props.onSave) {
      const res = await fvRef.current.validate();

      if (res === "Valid") {
        props.onSave(selectedGroupItem);
      }
    }
  };

  const handleClose = () => {
    if (props.onClose) {
      props.onClose();
    }
  };

  const createValidation = () => {
    if (formRef.current && !fvRef.current) {
      fvRef.current = createFormValidation(formRef.current, {
        fields: {
          Name: {
            validators: {
              notEmpty: { message: "Group name is required" }
            }
          }
        },
        plugins: {
          bootstrap5: new Bootstrap5(),
          trigger: new Trigger(),
          submitButton: new SubmitButton(),
        }
      }); 
    }
  };

  useEffect(() => {
    if (props.selectedGroupItem) {
      setSelectedGroupItem(props.selectedGroupItem);
    }
  }, [props.selectedGroupItem]);

  useEffect(() => { 
   if (formRef.current && !fvRef.current) {
      fvRef.current = null;
      createValidation(); 
   }
  }, [selectedGroupItem, formRef]);


  return (
    <>
      { selectedGroupItem ?
        (
          <div id={`menugroup-${selectedGroupItem?.MenuGroupId}`}>
            {!props.asModal && (
              <div className="panel__header white text-center">
                <span><b>Menu Group Details</b></span>
              </div>
            )}
            <Form id="MenuGroupForm" ref={formRef}>
              <Row>
                <Col md={12} xl={6} className="mt-3">
                  <FormLabel>Group Name</FormLabel>
                  <FormControl
                    type="text"
                    name="Name"
                    id="Name"
                    title="menu group name"
                    value={selectedGroupItem.Name}
                    onChange={(e) => updateGroupProperty(e.target.name, e.target.value)}
                  />
                </Col>
                <Col md={12} xl={6} className="mt-3">
                  <FormLabel>Enabled</FormLabel>
                  <FormCheckInput
                    name="Enabled"
                    id="Enabled"
                    title="set enabled"
                    checked={selectedGroupItem.Enabled}
                    onChange={(e) => updateGroupProperty(e.target.name, e.target.checked)}
                    className="form-check"
                  />
                </Col>
              </Row>
              <Row>
                <Col md={12} className="mt-3">
                  <FormLabel>Menu Logo Image</FormLabel>
                  <SiteMediaPicker 
                    onMediaClear={() => updateGroupProperty("Logo", "")}
                    onMediaSelect={(imgurl) => updateGroupProperty("Logo", imgurl)}
                    existingUrl={selectedGroupItem.Logo ?? ""}
                  />
                </Col>
              </Row>
              <Row>
                <Col md={12} xl={4} className="mt-3">
                  <FormLabel>Menu Background Colour</FormLabel>
                  <ColourPicker 
                    onColourChange={(colour) => updateGroupProperty("BackgroundColour", colour)}
                    selectedColour={selectedGroupItem.BackgroundColour}
                  />
                </Col>
                <Col md={12} xl={4} className="mt-3">
                  <FormLabel>Menu Text Colour</FormLabel>
                  <ColourPicker
                    onColourChange={(colour) => updateGroupProperty("TextColour", colour)}
                    selectedColour={selectedGroupItem.TextColour}
                  />
                </Col>
                <Col md={12} xl={4} className="mt-3">
                  <FormLabel>Menu Highlight Colour</FormLabel>
                  <ColourPicker
                    onColourChange={(colour) => updateGroupProperty("HighlightColour", colour)}
                    selectedColour={selectedGroupItem.HighlightColour}
                  />
                </Col>
              </Row>
              <Row>
                <Col md={12} xl={4} className="mt-3">
                  <FormLabel>Menu Item Alignment</FormLabel>
                  <FormSelect
                    name="Alignment"
                    id="Alignment"
                    title="select alignment for menu"
                    value={selectedGroupItem.Alignment ?? ""}
                    onChange={(e) => updateGroupProperty(e.target.name, e.target.value)}>
                      <option value="">Select alignment...</option>
                      <option value="Left">Left</option>
                      <option value="Middle">Middle</option>
                      <option value="Right">Right</option>
                  </FormSelect>
                </Col>
                <Col md={12} xl={4} className="mt-3">
                  <FormLabel>Show Shadow</FormLabel>
                  <FormCheckInput
                    id="Shadow"
                    name="Shadow"
                    checked={selectedGroupItem.Shadow}
                    onChange={(e) => updateGroupProperty(e.target.name, e.target.checked)}
                    title="shadow on or off for menu" 
                    className="form-check"
                  />
                </Col>
                <Col md={12} xl={4} className="mt-3">
                  <FormLabel>Sticky Menu (sticks to top of screen on scrolling)</FormLabel>
                  <FormCheckInput
                    name="KeepAtTop"
                    id="KeepAtTop"
                    title="keep the menu at the top the screen"
                    checked={selectedGroupItem.KeepAtTop}
                    className="form-check"
                    onChange={(e) => updateGroupProperty(e.target.name, e.target.checked)}
                  />
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <hr />
                </Col>
              </Row>
              <Row>
                <Col md={12} className="text-end">
                  <Button variant="outline-success" title="save group" className="me-1" onClick={handleSave}>
                    <FontAwesomeIcon icon={faSave} />&nbsp;Save
                  </Button>
                  <Button variant="outline-primary" title="close" onClick={handleClose}>
                    Close
                  </Button>
                </Col>
              </Row>
            </Form>
          </div>
        )
        :
        ( 
          null 
        )
      }
    </>
  );
}

export default MenuGroupEditor;
